import store from "@/store";
import DataService from "@/services/StrapiService";
import { normalizeVenueData } from "@/utils/functions";

/**
 * Redirect to the login page while keeping the target URL (path + query) so that
 * the user can return to it after a successful login.
 */
const redirectToLogin = (to, next) => {
  next({ path: "/login", query: { redirect: to.fullPath } });
};

const ifAuthenticated = (to, from, next) => {
  if (store.getters.isAuthenticated) {
    next();
    return;
  }
  redirectToLogin(to, next);
  return;
};

const ifAuthenticatedAndVenueIdChosen = (to, from, next) => {
  store.commit("setShowTopBarHeadline", to.meta.showInventoryTopBar || false);
  if (store.getters.isAuthenticated) {
    if (
      to.meta.roles == null ||
      to.meta.roles.indexOf(store.getters.getUser.role.name) > -1
    ) {
      if (store.getters.venueChosen) {
        next();
        return;
      } else {
        next("/venues");
        return;
      }
    }
  }
  redirectToLogin(to, next);
};

/**
 * Guard for routes that need a venue. If the route carries a `pvid` (poolinqVenueId) query param
 * and it differs from the currently stored venue, the venue is loaded and stored
 * before navigation proceeds.
 */
const ifAuthenticatedAndVenueIdChosenWithPvId = (to, from, next) => {
  store.commit("setShowTopBarHeadline", to.meta.showInventoryTopBar || false);
  if (!store.getters.isAuthenticated) {
    redirectToLogin(to, next);
    return;
  }
  if (
    to.meta.roles != null &&
    to.meta.roles.indexOf(store.getters.getUser.role.name) === -1
  ) {
    redirectToLogin(to, next);
    return;
  }
  const pvid = to.query.pvid;
  if (
    pvid != null &&
    pvid !== "" &&
    String(store.getters.getVenue.id) !== String(pvid)
  ) {
    DataService.getVenueDetail(pvid)
      .then((response) => {
        const venueData = normalizeVenueData(response["data"].data);
        store.commit("updateVenue", venueData);
        next();
      })
      .catch((err) => {
        console.log(err);
        next("/venues");
      });
    return;
  }
  if (store.getters.venueChosen) {
    next();
    return;
  }
  next("/venues");
};

export default [
  {
    path: "/",
    name: "Home",
    component: () => import("@/views/Home.vue"),
    beforeEnter: ifAuthenticatedAndVenueIdChosen,
    meta: {
      roles: ["Authenticated"],
    },
  },
  {
    path: "/register",
    name: "Register",
    component: () => import("@/views/Register.vue"),
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/Login.vue"),
  },
  {
    path: "/venues",
    name: "VenueChooser",
    component: () => import("@/views/VenueChooser.vue"),
    beforeEnter: ifAuthenticated,
    meta: {
      roles: ["Authenticated", "InventoryOnly"],
    },
  },
  {
    path: "/forgotpassword",
    name: "ForgotPassword",
    component: () => import("@/views/ForgotPassword.vue"),
  },
  {
    path: "/resetpassword",
    name: "ResetPassword",
    component: () => import("@/views/ResetPassword.vue"),
  },
  {
    path: "/wines",
    name: "Wines",
    component: () => import("@/views/VenueWines.vue"),
    beforeEnter: ifAuthenticatedAndVenueIdChosenWithPvId,
    props: (route) => ({
      playlistId: route.query.playlistId,
      smartListId: route.query.smartListId,
      pvid: route.query.pvid,
      pvwid: route.query.pvwid,
    }),
    meta: {
      roles: ["Authenticated"],
    },
  },
  {
    path: "/csv",
    name: "CSV",
    component: () => import("@/views/CSV.vue"),
    meta: {
      roles: ["Authenticated"],
    },
  },
  {
    path: "/insights",
    name: "Insights",
    component: () => import("@/views/Insights.vue"),
    beforeEnter: ifAuthenticatedAndVenueIdChosen,
    meta: {
      roles: ["Authenticated"],
    },
  },
  {
    path: "/reports",
    name: "Reports",
    component: () => import("@/views/Reports.vue"),
    beforeEnter: ifAuthenticatedAndVenueIdChosen,
    meta: {
      roles: ["Authenticated"],
    },
  },
  {
    path: "/winecard",
    name: "Weinkarte",
    component: () => import("@/views/Winecard.vue"),
    beforeEnter: ifAuthenticatedAndVenueIdChosen,
    meta: {
      roles: ["Authenticated"],
    },
  },
  {
    path: "/inventories",
    name: "Inventories",
    component: () => import("@/views/Inventories.vue"),
    beforeEnter: ifAuthenticatedAndVenueIdChosen,
    meta: {
      roles: ["Authenticated", "InventoryOnly"],
    },
  },
  {
    path: "/inventories/inventory/:id?",
    name: "Inventory",
    component: () => import("@/views/Inventory.vue"),
    beforeEnter: ifAuthenticatedAndVenueIdChosen,
    meta: {
      roles: ["Authenticated", "InventoryOnly"],
      showInventoryTopBar: true,
    },
  },
  {
    path: "/inventories/inventory/:id?/wines/:filterString?",
    name: "InventoryWines",
    component: () => import("@/views/InventoryWines.vue"),
    beforeEnter: ifAuthenticatedAndVenueIdChosen,
    props: (route) => ({ titleKey: route.query.titleKey }),
    meta: {
      roles: ["Authenticated", "InventoryOnly"],
      showInventoryTopBar: true,
    },
  },
  {
    path: "/updater",
    name: "Updater",
    component: () => import("@/views/Updater.vue"),
    beforeEnter: ifAuthenticatedAndVenueIdChosen,
    meta: {
      roles: ["Authenticated"],
    },
  },
  {
    path: "/receipts",
    name: "Receipts",
    component: () => import("@/views/Receipts.vue"),
    beforeEnter: ifAuthenticatedAndVenueIdChosenWithPvId,
    props: (route) => ({
      pvid: route.query.pvid, // poolinq venue id
    }),
    meta: {
      roles: ["Authenticated"],
    },
  },
  {
    path: "/receipts/matcher",
    name: "ReceiptMatcher",
    component: () => import("@/views/ReceiptMatcher.vue"),
    beforeEnter: ifAuthenticatedAndVenueIdChosenWithPvId,
    props: (route) => ({
      pvid: route.query.pvid, // poolinq venue id
      sheetType: route.query.sheet_type, //inbound or outbound
      receiptDate: route.query.receipt_date, //for outbound sheets
      invoiceNumber: route.query.invoice_number, //for inbound sheets
      invoiceDate: route.query.invoice_date, //for inbound sheets
    }),
    meta: {
      roles: ["Authenticated"],
    },
  },
];
